import React, { useRef, useCallback } from 'react';

interface ClickSparkProps {
  children: React.ReactNode;
  sparkColor?: string;
  sparkCount?: number;
  sparkSize?: number;
  duration?: number;
}

const ClickSpark: React.FC<ClickSparkProps> = ({
  children,
  sparkColor = '#667eea',
  sparkCount = 8,
  sparkSize = 6,
  duration = 600,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sparksRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      const sparksEl = sparksRef.current;
      if (!container || !sparksEl) return;

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      for (let i = 0; i < sparkCount; i++) {
        const spark = document.createElement('div');
        const angle = (360 / sparkCount) * i;
        const distance = 30 + Math.random() * 30;

        spark.style.cssText = `
          position: absolute;
          left: ${x}px;
          top: ${y}px;
          width: ${sparkSize}px;
          height: ${sparkSize}px;
          border-radius: 50%;
          background: ${sparkColor};
          pointer-events: none;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 ${sparkSize * 2}px ${sparkColor};
        `;

        sparksEl.appendChild(spark);

        const rad = (angle * Math.PI) / 180;
        const tx = Math.cos(rad) * distance;
        const ty = Math.sin(rad) * distance;

        spark.animate(
          [
            { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
            { transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0)`, opacity: 0 },
          ],
          { duration, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)', fill: 'forwards' }
        ).onfinish = () => spark.remove();
      }
    },
    [sparkColor, sparkCount, sparkSize, duration]
  );

  return (
    <div ref={containerRef} onClick={handleClick} style={{ position: 'relative', display: 'block', width: '100%' }}>
      <div ref={sparksRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 50 }} />
      {children}
    </div>
  );
};

export default ClickSpark;
